import * as React from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  /** Texto extra para la búsqueda (no visible), p. ej. código, cédula. */
  keywords?: string;
  disabled?: boolean;
}

export interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  id?: string;
  name?: string;
  /** Clases extra para el botón trigger. */
  className?: string;
  /** A partir de cuántas opciones se muestra el campo de búsqueda. Default 10. */
  searchThreshold?: number;
  emptyText?: string;
  /** Permite limpiar la selección (muestra una opción vacía). */
  clearable?: boolean;
  onBlur?: () => void;
}

/** Normaliza para búsqueda insensible a acentos y mayúsculas. */
function norm(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * Select con búsqueda. Cuando `options.length > searchThreshold` (10 por
 * defecto) muestra un campo de búsqueda que filtra por label + keywords.
 * No es un input nativo: en formularios react-hook-form envolver en `Controller`
 * (`value` / `onChange`). Sin dependencias externas (no Radix/floating-ui).
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = '— Selecciona —',
  disabled = false,
  loading = false,
  id,
  name,
  className,
  searchThreshold = 10,
  emptyText = 'Sin resultados',
  clearable = false,
  onBlur,
}: SearchableSelectProps): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);

  const rootRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const showSearch = options.length > searchThreshold;
  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = React.useMemo(() => {
    if (!query.trim()) return options;
    const q = norm(query);
    return options.filter(
      (o) => norm(o.label).includes(q) || (o.keywords !== undefined && norm(o.keywords).includes(q)),
    );
  }, [options, query]);

  // Cierra al hacer click fuera.
  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, onBlur]);

  // Al abrir: enfoca búsqueda y posiciona el highlight en el seleccionado.
  React.useEffect(() => {
    if (!open) return;
    setQuery('');
    const idx = selected ? options.findIndex((o) => o.value === selected.value) : 0;
    setActiveIndex(idx < 0 ? 0 : idx);
    const t = window.setTimeout(() => {
      if (showSearch) searchRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Mantiene el ítem activo visible.
  React.useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const commit = (opt: SelectOption): void => {
    if (opt.disabled === true) return;
    onChange(opt.value);
    setOpen(false);
    onBlur?.();
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      onBlur?.();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) commit(opt);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        id={id}
        name={name}
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && !loading && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 text-left text-sm text-text',
          'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      >
        <span className={cn('truncate', selected === null && 'text-text-muted')}>
          {loading ? 'Cargando…' : (selected?.label ?? placeholder)}
        </span>
        <ChevronDown className="h-4 w-4 flex-shrink-0 opacity-50" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[12rem] rounded-md border border-border bg-surface shadow-lg">
          {showSearch && (
            <div className="flex items-center gap-2 border-b border-border px-2.5 py-2">
              <Search className="h-4 w-4 flex-shrink-0 text-text-muted" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={onKeyDown}
                placeholder="Buscar…"
                className="w-full bg-transparent text-sm text-text outline-none placeholder:text-text-muted"
              />
              {query !== '' && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    searchRef.current?.focus();
                  }}
                  className="text-text-muted hover:text-text"
                  aria-label="Limpiar búsqueda"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          <ul ref={listRef} role="listbox" className="max-h-64 overflow-y-auto p-1">
            {clearable && (
              <li>
                <button
                  type="button"
                  onClick={() => commit({ value: '', label: placeholder })}
                  className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm text-text-muted hover:bg-surface-alt"
                >
                  {placeholder}
                </button>
              </li>
            )}
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-center text-sm text-text-muted">{emptyText}</li>
            ) : (
              filtered.map((opt, i) => {
                const isSelected = opt.value === value;
                const isActive = i === activeIndex;
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      data-idx={i}
                      role="option"
                      aria-selected={isSelected}
                      disabled={opt.disabled === true}
                      onClick={() => commit(opt)}
                      onMouseEnter={() => setActiveIndex(i)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-text',
                        isActive && 'bg-surface-alt',
                        opt.disabled === true && 'pointer-events-none opacity-50',
                      )}
                    >
                      <Check
                        className={cn(
                          'h-4 w-4 flex-shrink-0',
                          isSelected ? 'text-primary opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="truncate">{opt.label}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
